import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of, throwError } from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {ManufacturingSet} from "../../model/ManufacturingSet";
import {NotificationsService} from "./notifications.service";
import {AbstractLiveDataService} from "./abstract-live-data.service";
import {retryTransient, toServerCallError} from "./http-error";
import {LanguagePipe} from "../pipe/language.pipe";

@Injectable()

export class ManufacturingSetService extends AbstractLiveDataService<ManufacturingSet> {

  constructor(private httpa: HttpClient, private messageServicea: MessageService, private startupServicea: StartupService, protected notificationsService: NotificationsService) {
    super(httpa, messageServicea, startupServicea, notificationsService);
    this.notificationsService.setManufacturingSetService(this);
    this.notificationsService.startNotification();
  }

  makeObjectInstance(args: any): ManufacturingSet {
    const object = new ManufacturingSet(args);
    return object;
  }

  getLoggingObjectTypeName (): string {
    return "ManufacturingSet";
  }

  supportsParallel(): boolean {
    return true;
  }

  supportsLimitedParallel(): boolean {
    return true;
  }

  protected objectQualifiedPrimaryKeyId(): string {
    return 'manufacturingSet.ID';
  }

  getURLEndPoint (): string {
    return "manufacturingset";
  }

  getLinkData(): any[] {
    return [
      { action: 'AddUpdateArray', serviceName: 'printNodeOrderItemService', arrayProperty: 'printNodeOrderItems'},
      { action: 'Link', objectIdPropertyName: 'manufacturingTaskBucketPeriod_id', serviceName: 'manufacturingTaskBucketPeriodService', arrayProperty: 'manufacturingSets', objectProperty: 'manufacturingTaskBucketPeriod'},
      { action: 'AddUpdateArray', serviceName: 'manufacturingTaskService', arrayProperty: 'manufacturingTasks'},
    ];
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleLocalError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** PUT: update the manufacturingSet on the server */
  setManufacturingProcess (manufacturingSetId: number, manufacturingProcessId: number): Observable<ManufacturingSet> {
    const url = `${environment.apiBaseURL}manufacturingset/setmanufacturingprocess/${manufacturingSetId}/${manufacturingProcessId}`;
    this.messageService.add(`ManufacturingSetService: setManufacturingProcess manufacturingSetId=${manufacturingSetId}  manufacturingProcessId=${manufacturingProcessId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingSet => {
          const m = new ManufacturingSet(manufacturingSet);
          return m;
        }),
        catchError(this.handleLocalError<any>('setManufacturingProcess'))
      );
  }

  /** PUT: move a PrintNodeOrderItem to a specific ManufacturingSet *
  movePrintNodeOrderItem (manufacturingSetId: number, manufacturingSetDetails: any,  printNodeOrderItemId: number): Observable<ManufacturingSet> {
    const url = `${environment.apiBaseURL}manufacturingset/addprintnodeorderitem/${manufacturingSetId}/${printNodeOrderItemId}`;
    this.messageService.add(`ManufacturingSetService: addPrintNodeOrderItem manufacturingSetId=${manufacturingSetId}  manufacturingProcessId=${printNodeOrderItemId}`);
    return this.http.put(url, manufacturingSetDetails, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingSet => {
          const m = new ManufacturingSet(manufacturingSet);
          return m;
        }),
        catchError(this.handleLocalError<any>('addPrintNodeOrderItem'))
      );
  }
  /** PUT: update the manufacturingSet on the server */
  removePrintNodeOrderItem (manufacturingSetId: number, printNodeOrderItemId: number): Observable<ManufacturingSet> {
    const url = `${environment.apiBaseURL}manufacturingset/removeprintnodeorderitem/${manufacturingSetId}/${printNodeOrderItemId}`;
    this.messageService.add(`ManufacturingSetService: removePrintNodeOrderItem manufacturingSetId=${manufacturingSetId}  manufacturingProcessId=${printNodeOrderItemId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingSet => {
          const m = new ManufacturingSet(manufacturingSet);
          return m;
        }),
        catchError(this.handleLocalError<any>('addPrintNodeOrderItem'))
      );
  }

  /** PUT: update the manufacturingSet on the server */
  setOpenStatus (manufacturingSetId: number, openStatus: string): Observable<ManufacturingSet> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/setopenstatus/${manufacturingSetId}/${openStatus}`;
    this.messageService.add(`ManufacturingSetService: setOpenStatus manufacturingSetId=${manufacturingSetId}  manufacturingProcessId=${openStatus}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingSet => {
          const m = new ManufacturingSet(manufacturingSet);
          return m;
        }),
        catchError(this.handleErrorX<any>('setOpenStatus'))
      );
  }

  /**
   * Set the ManufacturingSet's `status` — today only used to put a
   * reopened set back to `INITIAL`.
   *
   * ---------------------------------------------------------------------
   * WHY THIS EXISTS
   * ---------------------------------------------------------------------
   * A set can only be STARTED after it has been CLOSED, so `STARTED` +
   * `OPEN` is not a normal state: it means the set was **reopened after
   * work had begun**. That is a legitimate thing to do — a bad print run,
   * a PDF that has to be remade — and the server already does half of the
   * cleanup for it. `ManufacturingSets.setOpenStatus` resets every
   * ManufacturingTask under the set back to `INITIAL` when it reopens a
   * CLOSED set.
   *
   * What it does not do is reset the **set's own** status. So the set
   * reads STARTED while every task inside it reads INITIAL, and the board
   * shows work in progress that nothing is actually working on. This
   * endpoint is how an operator reconciles that.
   *
   * ---------------------------------------------------------------------
   * WHY THE GENERIC PUT AND NOT A DEDICATED ROUTE
   * ---------------------------------------------------------------------
   * There is no `setstatus` route — `setopenstatus` is the only status
   * endpoint. The generic `PUT /manufacturingset/:id` hands the request
   * body straight to `getRepository().save()`, so a payload of just
   * `{ID, status}` is a partial column update and nothing else is
   * touched.
   *
   * **The payload is minimal deliberately, and it is not a style choice.**
   * Sending the loaded `ManufacturingSet` would (a) serialise the whole
   * object graph, and `PrintNodeOrderItem.manufacturingSet` points back at
   * the set, so `JSON.stringify` hits a cycle and throws, and (b) echo
   * every other column back, which is the stale-read overwrite hazard the
   * server described for `PUT /project`. Two fields go, two fields change.
   *
   * A dedicated `PUT /manufacturingset/setstatus/:id/:status` has been
   * requested so the transition can be validated server-side; until then
   * the guard lives in the UI. See outbox
   * `2026-08-27-set-status-reset-and-open-started-policy.md`.
   *
   * The response is the payload echoed back, **not** a populated set, so
   * it is deliberately typed `any` and must not be fed to
   * `new ManufacturingSet(...)` — doing so would produce an object whose
   * every other field is a default and stamp that over the cache. The
   * server sends a MANUFACTURING_SET UPDATE notification, which is what
   * actually refreshes the board.
   */
  setStatus (manufacturingSetId: number, status: string): Observable<any> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${manufacturingSetId}`;
    this.messageService.add(`ManufacturingSetService: setStatus manufacturingSetId=${manufacturingSetId} status=${status}`);
    // PUT is idempotent, so retrying a transient failure cannot duplicate
    // anything — unlike the POST paths, which are capped at one retry.
    return this.http.put<any>(url, { ID: manufacturingSetId, status: status }, this.startupService.getHttpOptions())
      .pipe(
        retryTransient<any>(2, 400),
        catchError(error => {
          const wrapped = toServerCallError(error, 'setStatus',
            LanguagePipe.get('The set status could not be changed.'));
          console.error('setStatus failed', wrapped.statusCode, wrapped.code, wrapped.details, error);
          return throwError(wrapped);
        })
      );
  }

  private handleErrorX<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
